import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { doc, getDoc } from 'firebase/firestore';
import { X, Plus, Trash2, Layers, FileText, ListOrdered, Hammer } from 'lucide-react';
import { db } from '../../utils/firebase';
import type {
  ChangeOrder,
  ChangeOrderBillingMode,
  ChangeOrderLineItem,
  ChangeOrderPricingMode,
  ProjectPLBucket,
  ProposalBuilderData,
  ProposalNodeSet,
} from '../../types';
import { PROJECT_PL_BUCKETS } from '../../types';
import { resolveChangeOrderBillingMode } from '../../utils/paymentSchedule';
import { getProposalRates, ProposalRateSettings } from '../../utils/appSettings';
import { applyProfitMargin, calculateTotals } from '../../utils/proposalCalculations';
import { suggestChangeOrderLineBucket } from '../../utils/changeOrderLineBucket';
import {
  computeChangeOrderBudgetByBucket,
  splitChangeOrderDelta,
} from '../../utils/contractValue';
import { effectiveBondingPercent, formatBondingPercent } from '../../utils/proposalBonding';
import {
  createChangeOrder,
  formatChangeOrderSaveError,
  getChangeOrderReference,
  updateChangeOrder,
} from '../../utils/changeOrders';
import StructureDetailModal from './StructureDetailModal';
import './ChangeOrder.css';

const BUCKET_LABELS: Record<ProjectPLBucket, string> = {
  design: 'Design',
  engineering: 'Engineering',
  timber: 'Timber / Materials',
  production: 'Production',
  freight: 'Freight',
  install: 'Install',
  travel: 'Travel',
  overhead: 'Overhead',
  unassigned: 'Other / Unassigned',
};

const fmt = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;

const newLineId = () => `coli-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const AUTO_SAVE_DEBOUNCE_MS = 600;

type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** Default profit margin for a new cost-based line item (matches builder default). */
const DEFAULT_LINE_MARGIN = 40;

/** Recompute a line's billable price/amount and raw cost from cost + margin. */
function recalcLine(li: ChangeOrderLineItem): ChangeOrderLineItem {
  const qty = Number(li.quantity) || 0;
  const unitCost = Number(li.unitCost) || 0;
  const margin = Number.isFinite(Number(li.margin)) ? Number(li.margin) : 0;
  const unitPrice = applyProfitMargin(unitCost, margin);
  return {
    ...li,
    margin,
    unitPrice,
    amount: Math.round(qty * unitPrice),
    costEstimate: Math.round(qty * unitCost),
  };
}

/**
 * Bring a stored line item into the cost+margin model. Legacy lines were priced
 * directly (unitPrice only); treat that price as the cost at 0% margin so the
 * billable amount is preserved until the user re-prices it.
 */
function normalizeLine(li: ChangeOrderLineItem): ChangeOrderLineItem {
  const withCost =
    typeof li.unitCost === 'number'
      ? li
      : { ...li, unitCost: Number(li.unitPrice) || 0, margin: li.margin ?? 0 };
  return recalcLine({
    ...withCost,
    margin: Number.isFinite(Number(withCost.margin)) ? Number(withCost.margin) : 0,
  });
}

function cloneNodeSet(ns: ProposalNodeSet): ProposalNodeSet {
  const copy: ProposalNodeSet = JSON.parse(JSON.stringify(ns));
  copy.id = `nodeset-co-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  return copy;
}

/**
 * Numeric input that keeps a raw text buffer so partial input — including a
 * leading "-" for credits — is preserved while typing. A controlled
 * `type="number"` field resets to 0 mid-keystroke, which makes negatives
 * impossible to enter.
 */
function NumberField({
  value,
  onChange,
  id,
  className,
  placeholder,
  disabled,
  allowNegative = true,
  allowEmpty = false,
  showZero = false,
}: {
  value: number | undefined;
  onChange: (n: number | undefined) => void;
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  allowNegative?: boolean;
  allowEmpty?: boolean;
  /** When true, display 0 as "0" instead of blank (use for margin % fields). */
  showZero?: boolean;
}) {
  const display = (v: number | undefined) => {
    if (v === undefined) return '';
    if (v === 0 && !showZero) return '';
    return String(v);
  };
  const [text, setText] = useState<string>(display(value));

  // Re-sync when the external value changes to something the buffer doesn't represent.
  useEffect(() => {
    const parsed = parseFloat(text);
    const current = Number.isNaN(parsed) ? (allowEmpty ? undefined : 0) : parsed;
    if (current !== value) setText(display(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handle = (raw: string) => {
    if (raw === '') {
      setText('');
      onChange(allowEmpty ? undefined : 0);
      return;
    }
    const re = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
    if (!re.test(raw)) return;
    setText(raw);
    if (raw === '-' || raw === '.' || raw === '-.') {
      onChange(allowEmpty ? undefined : 0);
      return;
    }
    const n = parseFloat(raw);
    onChange(Number.isNaN(n) ? (allowEmpty ? undefined : 0) : n);
  };

  return (
    <input
      id={id}
      className={className}
      type="text"
      inputMode={allowNegative ? 'text' : 'decimal'}
      value={text}
      placeholder={placeholder}
      onChange={e => handle(e.target.value)}
      disabled={disabled}
    />
  );
}

export interface ChangeOrderEditorProps {
  dealId: string;
  projectId?: string;
  clientName?: string;
  clientEmail?: string;
  projectName?: string;
  projectNumber?: string;
  /** Existing change order to edit; omit for a new one. */
  existing?: ChangeOrder | null;
  /** Signed proposal base + other approved COs (excludes the one being edited). */
  baseContractValue: number;
  currentUserUid: string;
  currentUserName?: string;
  onClose: () => void;
  onSaved?: (co: ChangeOrder) => void;
  /** Called when the user wants to send for signature (after save). */
  onSend?: (co: ChangeOrder) => void;
}

export default function ChangeOrderEditor({
  dealId,
  projectId,
  clientName,
  clientEmail,
  projectName,
  projectNumber,
  existing,
  baseContractValue,
  currentUserUid,
  currentUserName,
  onClose,
  onSaved,
  onSend,
}: ChangeOrderEditorProps) {
  const readOnly = !!existing && existing.status !== 'draft';

  const [savedDraft, setSavedDraft] = useState<ChangeOrder | null>(existing ?? null);
  const [draftId, setDraftId] = useState<string | undefined>(existing?.id);
  const draftIdRef = useRef<string | undefined>(existing?.id);

  const [title, setTitle] = useState(existing?.title || '');
  const [scope, setScope] = useState(existing?.scopeDescription || '');
  const [pricingMode, setPricingMode] = useState<ChangeOrderPricingMode>(existing?.pricingMode || 'lump_sum');
  const [lumpSumAmount, setLumpSumAmount] = useState<number>(existing?.lumpSumAmount ?? 0);
  const [lumpSumBucket, setLumpSumBucket] = useState<ProjectPLBucket>(
    (existing?.budgetCostByBucket && (Object.keys(existing.budgetCostByBucket)[0] as ProjectPLBucket)) || 'unassigned',
  );
  const [lineItems, setLineItems] = useState<ChangeOrderLineItem[]>((existing?.lineItems || []).map(normalizeLine));
  const [builderNodeSets, setBuilderNodeSets] = useState<ProposalNodeSet[]>(existing?.builderNodeSets || []);
  const [builderMargin, setBuilderMargin] = useState<number>(existing?.builderProfitMargin ?? 40);
  const [billingMode, setBillingMode] = useState<ChangeOrderBillingMode>(
    existing?.billingMode || 'immediate',
  );

  const [rates, setRates] = useState<ProposalRateSettings | null>(null);
  const [proposal, setProposal] = useState<ProposalBuilderData | null>(null);
  const [editingStructureId, setEditingStructureId] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // Saved lines keep the category already chosen. New lines follow the description
  // until someone sets the category dropdown themselves.
  const manualBucketIds = useRef<Set<string>>(
    new Set((existing?.lineItems || []).map(li => li.id)),
  );
  const saveInProgressRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const saveTailRef = useRef<Promise<ChangeOrder | null>>(Promise.resolve(null));
  const runAutoSaveRef = useRef<() => Promise<void>>(async () => undefined);

  useEffect(() => {
    if (!existing?.id) return;
    draftIdRef.current = existing.id;
    setDraftId(existing.id);
    setSavedDraft(existing);
  }, [existing?.id, existing]);

  useEffect(() => {
    getProposalRates().then(setRates).catch(() => setRates(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, 'proposalBuilders', dealId))
      .then(snap => {
        if (cancelled) return;
        if (snap.exists()) setProposal(snap.data() as ProposalBuilderData);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  // Bonding percent from the proposal's Global Costs. Drafts follow the live
  // proposal; sent/approved COs keep the percent they were priced with.
  const bondingPercent = readOnly
    ? (Number(existing?.bondingPercent) || 0)
    : effectiveBondingPercent(proposal?.globalCosts);

  const previewCO = useMemo(
    () => ({
      pricingMode,
      lumpSumAmount,
      lineItems,
      builderNodeSets,
      builderProfitMargin: builderMargin,
      bondingPercent,
      deltaAmount: 0,
    }),
    [pricingMode, lumpSumAmount, lineItems, builderNodeSets, builderMargin, bondingPercent],
  );

  const deltaSplit = useMemo(() => splitChangeOrderDelta(previewCO), [previewCO]);
  const delta = deltaSplit.total;
  const newContractValue = baseContractValue + delta;

  // Credits can never bill immediately — QB has no negative invoices.
  const effectiveBillingMode = resolveChangeOrderBillingMode({ billingMode, deltaAmount: delta });
  const billingModeLocked = delta < 0;

  // ── Line item handlers ──────────────────────────────────────────────────
  const addLineItem = () => {
    setLineItems(prev => [
      ...prev,
      recalcLine({
        id: newLineId(),
        description: '',
        bucket: 'unassigned',
        quantity: 1,
        unitCost: 0,
        margin: DEFAULT_LINE_MARGIN,
        unitPrice: 0,
        amount: 0,
        costEstimate: 0,
      }),
    ]);
  };
  const updateLineItem = (id: string, patch: Partial<ChangeOrderLineItem>) => {
    setLineItems(prev =>
      prev.map(li => (li.id === id ? recalcLine({ ...li, ...patch }) : li)),
    );
  };
  const removeLineItem = (id: string) => setLineItems(prev => prev.filter(li => li.id !== id));

  // Cost / billable / profit roll-up for the line-item summary.
  const lineTotals = useMemo(() => {
    const cost = lineItems.reduce((s, li) => s + (Number(li.costEstimate) || 0), 0);
    const billable = lineItems.reduce((s, li) => s + (Number(li.amount) || 0), 0);
    const profit = billable - cost;
    const blendedMargin = billable !== 0 ? Math.round((profit / billable) * 100) : 0;
    return { cost, billable, profit, blendedMargin };
  }, [lineItems]);

  // ── Builder handlers ────────────────────────────────────────────────────
  const importStructures = useCallback(
    (ids: string[]) => {
      if (!proposal?.nodeSets) return;
      const picked = proposal.nodeSets.filter(ns => ids.includes(ns.id)).map(cloneNodeSet);
      setBuilderNodeSets(prev => [...prev, ...picked]);
      setShowImport(false);
    },
    [proposal],
  );
  const updateStructure = (id: string, updates: Partial<ProposalNodeSet>) => {
    setBuilderNodeSets(prev =>
      prev.map(ns => {
        if (ns.id !== id) return ns;
        const merged: ProposalNodeSet = { ...ns, ...updates };
        if (updates.install) merged.install = { ...ns.install, ...updates.install };
        if (updates.overhead) merged.overhead = { ...ns.overhead, ...updates.overhead };
        return merged;
      }),
    );
  };
  const removeStructure = (id: string) => setBuilderNodeSets(prev => prev.filter(ns => ns.id !== id));

  const structureBillable = (ns: ProposalNodeSet): number => {
    const totals = calculateTotals([ns], builderMargin);
    return totals.billableTotal;
  };

  // ── Persistence ─────────────────────────────────────────────────────────
  const buildPayload = useCallback((): Partial<ChangeOrder> => {
    // Sign-preserving: a lump-sum credit REDUCES the bucket's budget (P&L,
    // WIP est. cost, PO budgets). The old Math.abs here turned credits into
    // phantom budget additions.
    const budgetCostByBucket =
      pricingMode === 'lump_sum'
        ? { [lumpSumBucket]: Math.round(lumpSumAmount) }
        : computeChangeOrderBudgetByBucket(previewCO);
    return {
      title: title.trim() || 'Change Order',
      scopeDescription: scope.trim() || undefined,
      pricingMode,
      lumpSumAmount: pricingMode === 'lump_sum' ? Math.round(lumpSumAmount) : undefined,
      lineItems: pricingMode === 'line_item' ? lineItems.map(recalcLine) : undefined,
      builderNodeSets: pricingMode === 'builder' ? builderNodeSets : undefined,
      builderProfitMargin: pricingMode === 'builder' ? builderMargin : undefined,
      deltaAmount: delta,
      // Persist bonding only when the proposal bonds; COs on unbonded jobs keep their old shape.
      ...(bondingPercent > 0
        ? { bondingPercent, bondingAdjustment: deltaSplit.bonding }
        : existing?.bondingPercent !== undefined
          ? { bondingPercent: 0, bondingAdjustment: 0 }
          : {}),
      budgetCostByBucket,
      billingMode: resolveChangeOrderBillingMode({ billingMode, deltaAmount: delta }),
    };
  }, [pricingMode, lumpSumBucket, lumpSumAmount, previewCO, title, scope, lineItems, builderNodeSets, builderMargin, delta, deltaSplit.bonding, bondingPercent, existing?.bondingPercent, billingMode]);

  const validateForSend = useCallback((): string | null => {
    if (!title.trim()) return 'Add a title for this change order.';
    if (pricingMode === 'lump_sum' && !lumpSumAmount) return 'Enter a lump sum amount.';
    if (pricingMode === 'lump_sum' && lumpSumBucket === 'unassigned') {
      return 'Pick the cost category this amount changes.';
    }
    if (pricingMode === 'line_item' && lineItems.length === 0) return 'Add at least one line item.';
    if (pricingMode === 'line_item' && lineItems.some(li => !li.bucket || li.bucket === 'unassigned')) {
      return 'Pick a cost category for each line. Labor, materials, design, and stamps fill in from the description.';
    }
    if (pricingMode === 'builder' && builderNodeSets.length === 0) return 'Add at least one structure.';
    if (delta === 0) {
      return 'Contract value change is $0 — check quantities and unit costs. (0% profit margin is allowed.)';
    }
    return null;
  }, [title, pricingMode, lumpSumAmount, lumpSumBucket, lineItems, builderNodeSets.length, delta]);

  const hasDraftContent = useMemo(
    () =>
      !!title.trim()
      || !!scope.trim()
      || lineItems.length > 0
      || lumpSumAmount !== 0
      || builderNodeSets.length > 0,
    [title, scope, lineItems.length, lumpSumAmount, builderNodeSets.length],
  );

  const formFingerprint = useMemo(
    () =>
      JSON.stringify({
        title,
        scope,
        pricingMode,
        lumpSumAmount,
        lumpSumBucket,
        lineItems,
        builderNodeSets,
        builderMargin,
        billingMode,
      }),
    [title, scope, pricingMode, lumpSumAmount, lumpSumBucket, lineItems, builderNodeSets, builderMargin, billingMode],
  );
  const initialFingerprintRef = useRef(formFingerprint);
  const formFingerprintRef = useRef(formFingerprint);

  useEffect(() => {
    formFingerprintRef.current = formFingerprint;
  }, [formFingerprint]);

  const markAutoSaveSaved = useCallback(() => {
    setAutoSaveStatus('saved');
    if (savedStatusTimerRef.current) clearTimeout(savedStatusTimerRef.current);
    savedStatusTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 2000);
  }, []);

  const persistDraft = useCallback(
    async (options?: { forSend?: boolean }): Promise<ChangeOrder | null> => {
      const op = saveTailRef.current.then(async (): Promise<ChangeOrder | null> => {
        if (readOnly) return savedDraft;

        if (options?.forSend) {
          const v = validateForSend();
          if (v) {
            setError(v);
            return null;
          }
        } else if (!hasDraftContent && !draftIdRef.current) {
          return null;
        }

        if (!options?.forSend) setError(null);
        setAutoSaveStatus('saving');
        saveInProgressRef.current = true;

        try {
          const payload = buildPayload();
          const id = draftIdRef.current;

          if (id) {
            const base = savedDraft || existing;
            await updateChangeOrder(id, payload, { recomputeFrom: base ?? undefined });
            const co = { ...(base || {}), id, dealId, ...payload, status: 'draft' as const } as ChangeOrder;
            setSavedDraft(co);
            if (options?.forSend) setAutoSaveStatus('idle');
            else markAutoSaveSaved();
            initialFingerprintRef.current = formFingerprintRef.current;
            return co;
          }

          const created = await createChangeOrder({
            dealId,
            projectId,
            proposalId: dealId,
            clientName,
            clientEmail,
            projectName,
            projectNumber,
            createdBy: currentUserUid,
            title: payload.title || 'Change Order',
            scopeDescription: payload.scopeDescription,
            pricingMode,
            lumpSumAmount: payload.lumpSumAmount,
            lineItems: payload.lineItems,
            builderNodeSets: payload.builderNodeSets,
            builderProfitMargin: payload.builderProfitMargin,
            bondingPercent: payload.bondingPercent,
            budgetCostByBucket: payload.budgetCostByBucket,
          });
          draftIdRef.current = created.id;
          setDraftId(created.id);
          setSavedDraft(created);
          if (options?.forSend) setAutoSaveStatus('idle');
          else markAutoSaveSaved();
          initialFingerprintRef.current = formFingerprintRef.current;
          return created;
        } catch (err) {
          console.error('[ChangeOrderEditor] save failed', err);
          setAutoSaveStatus('error');
          setError(formatChangeOrderSaveError(err));
          return null;
        } finally {
          saveInProgressRef.current = false;
          if (pendingSaveRef.current) {
            pendingSaveRef.current = false;
            queueMicrotask(() => void runAutoSaveRef.current());
          }
        }
      });

      saveTailRef.current = op.catch(() => null);
      return op;
    },
    [
      readOnly,
      savedDraft,
      existing,
      hasDraftContent,
      buildPayload,
      validateForSend,
      dealId,
      projectId,
      clientName,
      clientEmail,
      projectName,
      projectNumber,
      currentUserUid,
      pricingMode,
      markAutoSaveSaved,
    ],
  );

  const persistDraftRef = useRef(persistDraft);
  persistDraftRef.current = persistDraft;

  const runAutoSave = useCallback(async () => {
    if (readOnly) return;
    await persistDraftRef.current();
  }, [readOnly]);

  runAutoSaveRef.current = runAutoSave;

  useEffect(() => {
    if (readOnly) return;
    if (formFingerprint === initialFingerprintRef.current) return;

    if (saveInProgressRef.current) {
      pendingSaveRef.current = true;
      return;
    }

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      void runAutoSaveRef.current();
    }, AUTO_SAVE_DEBOUNCE_MS);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [formFingerprint, readOnly]);

  useEffect(
    () => () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (savedStatusTimerRef.current) clearTimeout(savedStatusTimerRef.current);
    },
    [],
  );

  const waitForSaveInProgress = useCallback(async () => {
    await saveTailRef.current.catch(() => null);
  }, []);

  const flushAutoSave = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = undefined;
    }
    if (readOnly) return;
    await waitForSaveInProgress();
    if (formFingerprintRef.current === initialFingerprintRef.current) return;
    await persistDraftRef.current();
  }, [readOnly, waitForSaveInProgress]);

  const handleClose = useCallback(async () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    await flushAutoSave();
    onClose();
  }, [flushAutoSave, onClose]);

  const handleSend = async () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    await waitForSaveInProgress();
    const co = await persistDraftRef.current({ forSend: true });
    if (co?.id) {
      onSaved?.(co);
      onSend?.(co);
    }
  };

  const editingStructure = builderNodeSets.find(ns => ns.id === editingStructureId) || null;

  const headerLabel = savedDraft?.id
    ? getChangeOrderReference(savedDraft)
    : `New Change Order${projectNumber ? ` · ${projectNumber}` : ''}`;

  const autoSaveLabel =
    autoSaveStatus === 'saving'
      ? 'Saving…'
      : autoSaveStatus === 'saved'
        ? 'Draft saved'
        : autoSaveStatus === 'error'
          ? 'Save failed'
          : draftId
            ? 'All changes saved'
            : hasDraftContent
              ? 'Unsaved changes'
              : '';

  return createPortal(
    <div className="co-editor-overlay" onMouseDown={e => e.target === e.currentTarget && void handleClose()}>
      <div className="co-editor" role="dialog" aria-modal="true" aria-label="Change order editor">
        <div className="co-editor-header">
          <div className="co-editor-header-titles">
            <span className="co-editor-eyebrow">{headerLabel}</span>
            <span className="co-editor-title">{projectName || clientName || 'Change Order'}</span>
          </div>
          <button className="co-editor-close" onClick={() => void handleClose()} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="co-editor-body">
          {readOnly && (
            <div className="co-empty" style={{ borderStyle: 'solid' }}>
              This change order is {existing?.status} and can no longer be edited.
            </div>
          )}

          <div className="co-field">
            <label className="co-field-label" htmlFor="co-title">Title</label>
            <input
              id="co-title"
              className="co-input"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Add covered porch & upgrade to White Oak"
              disabled={readOnly}
            />
          </div>

          <div className="co-field">
            <label className="co-field-label" htmlFor="co-scope">Scope of work</label>
            <textarea
              id="co-scope"
              className="co-textarea"
              value={scope}
              onChange={e => setScope(e.target.value)}
              placeholder="Describe what is being added, removed, or changed and why."
              disabled={readOnly}
            />
          </div>

          <div className="co-field">
            <span className="co-field-label">Pricing method</span>
            <div className="co-mode-switch" role="tablist">
              {([
                { id: 'lump_sum', label: 'Lump Sum', icon: <FileText size={14} /> },
                { id: 'line_item', label: 'Line Items', icon: <ListOrdered size={14} /> },
                { id: 'builder', label: 'Proposal Builder', icon: <Hammer size={14} /> },
              ] as { id: ChangeOrderPricingMode; label: string; icon: ReactNode }[]).map(m => (
                <button
                  key={m.id}
                  className={`co-mode-btn ${pricingMode === m.id ? 'active' : ''}`}
                  onClick={() => !readOnly && setPricingMode(m.id)}
                  disabled={readOnly}
                  type="button"
                >
                  {m.icon}
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Lump sum */}
          {pricingMode === 'lump_sum' && (
            <div className="co-field-row">
              <div className="co-field">
                <label className="co-field-label" htmlFor="co-lump">Amount (use a negative value for a credit)</label>
                <div className="co-lump-input-wrap">
                  <span className="co-lump-prefix">$</span>
                  <NumberField
                    id="co-lump"
                    className="co-input co-lump-input"
                    value={lumpSumAmount}
                    onChange={n => setLumpSumAmount(n ?? 0)}
                    disabled={readOnly}
                    allowNegative
                  />
                </div>
              </div>
              <div className="co-field">
                <label className="co-field-label" htmlFor="co-lump-bucket">Budget impact (project P&amp;L cost category)</label>
                <select
                  id="co-lump-bucket"
                  className="co-select"
                  value={lumpSumBucket}
                  onChange={e => setLumpSumBucket(e.target.value as ProjectPLBucket)}
                  disabled={readOnly}
                >
                  {PROJECT_PL_BUCKETS.map(b => (
                    <option key={b} value={b}>{BUCKET_LABELS[b]}</option>
                  ))}
                </select>
                <span className="co-field-hint">
                  {lumpSumAmount < 0
                    ? `Credit: reduces the ${BUCKET_LABELS[lumpSumBucket]} budget by ${fmt(Math.abs(lumpSumAmount))}.`
                    : `Adds ${fmt(lumpSumAmount)} to the ${BUCKET_LABELS[lumpSumBucket]} budget.`}
                </span>
              </div>
            </div>
          )}

          {/* Line items — cost-based: enter unit cost + margin, price is derived */}
          {pricingMode === 'line_item' && (
            <div className="co-line-table">
              <div className="co-line-head">
                <span>Description</span>
                <span>Category</span>
                <span>Qty</span>
                <span>Unit cost</span>
                <span>Margin %</span>
                <span>Amount</span>
                <span />
              </div>
              {lineItems.map(li => (
                <div className="co-line-row" key={li.id}>
                  <input
                    className="co-line-cell-input"
                    value={li.description}
                    onChange={e => {
                      const description = e.target.value;
                      const patch: Partial<ChangeOrderLineItem> = { description };
                      if (!manualBucketIds.current.has(li.id)) {
                        patch.bucket = suggestChangeOrderLineBucket(description);
                      }
                      updateLineItem(li.id, patch);
                    }}
                    placeholder="Description"
                    disabled={readOnly}
                  />
                  <select
                    className="co-line-cell-input"
                    value={li.bucket}
                    onChange={e => {
                      manualBucketIds.current.add(li.id);
                      updateLineItem(li.id, { bucket: e.target.value as ProjectPLBucket });
                    }}
                    disabled={readOnly}
                  >
                    {PROJECT_PL_BUCKETS.map(b => (
                      <option key={b} value={b}>{BUCKET_LABELS[b]}</option>
                    ))}
                  </select>
                  <NumberField
                    className="co-line-cell-input"
                    value={li.quantity}
                    onChange={n => updateLineItem(li.id, { quantity: n ?? 0 })}
                    disabled={readOnly}
                    allowNegative={false}
                  />
                  <NumberField
                    className="co-line-cell-input"
                    value={li.unitCost}
                    onChange={n => updateLineItem(li.id, { unitCost: n ?? 0 })}
                    disabled={readOnly}
                    allowNegative
                  />
                  <NumberField
                    className="co-line-cell-input"
                    value={li.margin}
                    onChange={n => updateLineItem(li.id, { margin: n ?? 0 })}
                    disabled={readOnly}
                    allowNegative={false}
                    showZero
                    placeholder="0"
                  />
                  <span
                    className={`co-line-amount ${li.amount < 0 ? 'negative' : ''}`}
                    title={`Unit price ${fmt(li.unitPrice)} · line cost ${fmt(li.costEstimate ?? 0)}`}
                  >
                    {fmt(li.amount)}
                  </span>
                  {!readOnly && (
                    <button className="co-line-del" onClick={() => removeLineItem(li.id)} aria-label="Remove line">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              {lineItems.length > 0 && (
                <div className="co-line-summary">
                  <span>Cost <strong>{fmt(lineTotals.cost)}</strong></span>
                  <span>Billable <strong>{fmt(lineTotals.billable)}</strong></span>
                  <span className={lineTotals.profit < 0 ? 'neg' : 'pos'}>
                    Profit <strong>{fmt(lineTotals.profit)}</strong> ({lineTotals.blendedMargin}%)
                  </span>
                </div>
              )}
              {!readOnly && (
                <button className="co-add-line" onClick={addLineItem} type="button">
                  <Plus size={14} /> Add line item
                </button>
              )}
              {!readOnly && (
                <span className="co-field-hint">
                  Category follows the description until you choose one. Labor and installation go to Install, materials to Timber, stamps to Engineering.
                </span>
              )}
            </div>
          )}

          {/* Builder */}
          {pricingMode === 'builder' && (
            <div className="co-field">
              <div className="co-builder-toolbar">
                {!readOnly && (
                  <button className="co-add-line" onClick={() => setShowImport(true)} type="button">
                    <Layers size={14} /> Import structures from proposal
                  </button>
                )}
                <div className="co-builder-margin">
                  <label htmlFor="co-margin">Profit margin %</label>
                  <NumberField
                    id="co-margin"
                    className="co-line-cell-input"
                    value={builderMargin}
                    onChange={n => setBuilderMargin(n ?? 0)}
                    disabled={readOnly}
                    allowNegative={false}
                    showZero
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="co-structure-list" style={{ marginTop: 10 }}>
                {builderNodeSets.length === 0 && (
                  <div className="co-empty">Import structures from the signed proposal to price this change order with the builder.</div>
                )}
                {builderNodeSets.map(ns => (
                  <div
                    className="co-structure-card"
                    key={ns.id}
                    onClick={() => !readOnly && setEditingStructureId(ns.id)}
                  >
                    <div>
                      <div className="co-structure-name">{ns.name}</div>
                      <div className="co-structure-meta">Qty {ns.quantity || 1}{readOnly ? '' : ' · click to edit'}</div>
                    </div>
                    <div className="co-structure-right">
                      <span className="co-structure-total">{fmt(structureBillable(ns))}</span>
                      {!readOnly && (
                        <button
                          className="co-line-del"
                          onClick={e => { e.stopPropagation(); removeStructure(ns.id); }}
                          aria-label="Remove structure"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Billing treatment */}
          <div className="co-field">
            <span className="co-field-label">Billing</span>
            <div className="co-mode-switch" role="tablist">
              {([
                {
                  id: 'immediate',
                  label: 'Invoice at approval',
                  hint: 'Creates its own QuickBooks invoice for the full amount when approved.',
                },
                {
                  id: 'contract_terms',
                  label: 'Fold into contract terms',
                  hint: 'No separate invoice — amount is spread across the remaining contract payment installments.',
                },
              ] as { id: ChangeOrderBillingMode; label: string; hint: string }[]).map(m => (
                <button
                  key={m.id}
                  className={`co-mode-btn ${effectiveBillingMode === m.id ? 'active' : ''}`}
                  onClick={() => !readOnly && !billingModeLocked && setBillingMode(m.id)}
                  disabled={readOnly || (billingModeLocked && m.id === 'immediate')}
                  title={m.hint}
                  type="button"
                >
                  {m.label}
                </button>
              ))}
            </div>
            {billingModeLocked && (
              <p className="co-structure-meta" style={{ marginTop: 6 }}>
                Credits always fold into the contract payment terms — QuickBooks cannot issue a
                negative invoice. The credit reduces the remaining installments.
              </p>
            )}
          </div>

          {error && (
            <div className="co-empty" style={{ borderColor: 'var(--pm-status-blocked, #b4453a)', color: 'var(--pm-status-blocked, #b4453a)', borderStyle: 'solid' }}>
              {error}
            </div>
          )}
        </div>

        <div className="co-editor-footer">
          <div className="co-totals">
            <div className="co-total-block">
              <span className="co-total-label">Current contract</span>
              <span className="co-total-value">{fmt(baseContractValue)}</span>
            </div>
            {bondingPercent > 0 ? (
              <>
                <div className="co-total-block">
                  <span className="co-total-label">Scope change</span>
                  <span className={`co-total-value ${deltaSplit.base < 0 ? 'delta-neg' : 'delta-pos'}`}>
                    {deltaSplit.base >= 0 ? '+' : ''}{fmt(deltaSplit.base)}
                  </span>
                </div>
                <div
                  className="co-total-block"
                  title={`Surety bonding pass-through at ${formatBondingPercent(bondingPercent)} of the scope change — set on the proposal's Global Costs.`}
                >
                  <span className="co-total-label">Bonding {formatBondingPercent(bondingPercent)}</span>
                  <span className={`co-total-value ${deltaSplit.bonding < 0 ? 'delta-neg' : 'delta-pos'}`}>
                    {deltaSplit.bonding >= 0 ? '+' : ''}{fmt(deltaSplit.bonding)}
                  </span>
                </div>
                <div className="co-total-block">
                  <span className="co-total-label">Total change</span>
                  <span className={`co-total-value ${delta < 0 ? 'delta-neg' : 'delta-pos'}`}>
                    {delta >= 0 ? '+' : ''}{fmt(delta)}
                  </span>
                </div>
              </>
            ) : (
              <div className="co-total-block">
                <span className="co-total-label">Change</span>
                <span className={`co-total-value ${delta < 0 ? 'delta-neg' : 'delta-pos'}`}>
                  {delta >= 0 ? '+' : ''}{fmt(delta)}
                </span>
              </div>
            )}
            <div className="co-total-block">
              <span className="co-total-label">New contract</span>
              <span className="co-total-value new-contract">{fmt(newContractValue)}</span>
            </div>
          </div>
          {!readOnly && (
            <div className="co-footer-actions">
              {autoSaveLabel && (
                <span
                  className={`co-autosave-status ${autoSaveStatus === 'error' ? 'is-error' : ''}`}
                  aria-live="polite"
                >
                  {autoSaveLabel}
                </span>
              )}
              <button className="co-icon-btn" onClick={() => void handleClose()} type="button">
                Close
              </button>
              <button className="co-btn-primary" onClick={() => void handleSend()} type="button">
                Send for signature
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Builder structure deep-editor */}
      {editingStructure && rates && (
        <StructureDetailModal
          dealId={dealId}
          nodeSet={editingStructure}
          allNodeSets={builderNodeSets}
          rates={rates}
          profitMargin={builderMargin}
          onClose={() => setEditingStructureId(null)}
          onUpdate={updates => updateStructure(editingStructure.id, updates)}
          onDelete={() => { removeStructure(editingStructure.id); setEditingStructureId(null); }}
          onRename={name => updateStructure(editingStructure.id, { name })}
          projectId={projectId}
        />
      )}

      {/* Import picker */}
      {showImport && (
        <ImportStructuresModal
          nodeSets={(proposal?.nodeSets || []).filter(ns => ns.enabled !== false)}
          onCancel={() => setShowImport(false)}
          onImport={importStructures}
        />
      )}
    </div>,
    document.body,
  );
}

function ImportStructuresModal({
  nodeSets,
  onCancel,
  onImport,
}: {
  nodeSets: ProposalNodeSet[];
  onCancel: () => void;
  onImport: (ids: string[]) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return createPortal(
    <div className="co-editor-overlay" onMouseDown={e => e.target === e.currentTarget && onCancel()}>
      <div className="co-editor" style={{ width: 'min(560px, 100%)' }}>
        <div className="co-editor-header">
          <div className="co-editor-header-titles">
            <span className="co-editor-eyebrow">Builder</span>
            <span className="co-editor-title">Import structures</span>
          </div>
          <button className="co-editor-close" onClick={onCancel} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="co-editor-body">
          {nodeSets.length === 0 && <div className="co-empty">No structures available on the proposal.</div>}
          <div className="co-structure-list">
            {nodeSets.map(ns => (
              <label className="co-structure-card" key={ns.id} style={{ cursor: 'pointer' }}>
                <div className="co-structure-name">{ns.name}</div>
                <input type="checkbox" checked={selected.has(ns.id)} onChange={() => toggle(ns.id)} />
              </label>
            ))}
          </div>
        </div>
        <div className="co-editor-footer">
          <span className="co-structure-meta">{selected.size} selected (copied as editable change-order scope)</span>
          <div className="co-footer-actions">
            <button className="co-icon-btn" onClick={onCancel} type="button">Cancel</button>
            <button
              className="co-btn-primary"
              onClick={() => onImport(Array.from(selected))}
              disabled={selected.size === 0}
              type="button"
            >
              Import {selected.size > 0 ? selected.size : ''}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
